"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Star, CircleCheck, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const testimonials = [{
  id: 1,
  name: "Michael Henderson",
  course: "Crawl Space Encapsulation",
  location: "Des Moines",
  rating: 5,
  text: "The moisture in our crawl space was out of control until we found the right professionals to step in.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/homeowner_michael.png",
  tags: ["Dry Home", "Professional"]
}, {
  id: 2,
  name: "Sarah Jenkins",
  course: "Vapor Barrier Installation",
  location: "Ankeny",
  rating: 5,
  text: "I was worried about mold growth under the house. They installed a heavy-duty vapor barrier and the difference is night and day. Highly recommend their local team.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/homeowner_sarah.png",
  tags: ["Mold Prevention", "Local"]
}, {
  id: 3,
  name: "David Miller",
  course: "Moisture Control System",
  location: "West Des Moines",
  rating: 5,
  text: "Excellent service from start to finish. They explained the process clearly and the encapsulation looks incredibly professional. No more musty smells!",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/homeowner_david.png",
  tags: ["No Odors", "Expertise"]
}, {
  id: 4,
  name: "Linda Thompson",
  course: "Crawl Space Insulation",
  location: "Urbandale",
  rating: 5,
  text: "Our floors are finally warm in the winter! The new insulation and sealing made a huge impact on our energy bills and overall comfort.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/homeowner_linda.png",
  tags: ["Energy Savings", "Comfort"]
}, {
  id: 5,
  name: "Robert Wilson",
  course: "Full Encapsulation",
  location: "Waukee",
  rating: 5,
  text: "The most thorough inspection I've ever had. They found issues other contractors missed and fixed them right the first time. Truly trustworthy.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/homeowner_robert.png",
  tags: ["Thorough", "Trustworthy"]
}];
export default function Customertestimonialsslider() {
  const [api, setApi] = useState<CarouselApi>();
  useEffect(() => {
    if (!api) {
      return;
    }
    const intervalId = setInterval(() => {
      api.scrollNext();
    }, 6000);
    return () => clearInterval(intervalId);
  }, [api]);
  return <section data-section-id="254696" id="customertestimonialsslider" className="bg-background text-foreground py-16 md:py-24 lg:py-[120px] overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary mb-4">
            What Des Moines Homeowners Say
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto rounded-md mb-6 opacity-80"></div>
          <p className="text-muted-foreground font-default max-w-2xl mx-auto text-lg">
            Read how our crawl space solutions have helped local families achieve healthier, dryer, and more energy-efficient homes.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <Card className="h-full flex flex-col shadow-md hover:shadow-lg transition-all duration-300 border-primary/20 bg-card">
                    <CardHeader className="flex flex-row items-start gap-4 pb-4 relative">
                      <Quote className="absolute top-4 right-4 size-8 text-primary/10 rotate-180" />
                      <SafeHydrate><Avatar className="size-14 border-2 border-primary/10 shadow-sm">
                        <AvatarImage src={testimonial.avatar} data-wvc-srcset={`${testimonial.avatar}-wvc-srcset`} data-wvc-sizes={`${testimonial.avatar}-wvc-sizes`} alt={`${testimonial.name} photo`} />
                        <AvatarFallback className="bg-primary/10 text-primary font-bold font-default text-lg">
                          {testimonial.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div className="flex flex-col pt-1">
                        <CardTitle className="text-lg font-default text-foreground">
                          {testimonial.name}
                        </CardTitle>
                        <CardDescription className="font-default text-sm font-medium text-primary mt-0.5">
                          {testimonial.course} • {testimonial.location}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-grow pb-6">
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, index) => <Star key={index} data-index={index} className="size-4 fill-primary text-primary" />)}
                      </div>
                      <p className="text-muted-foreground font-default leading-relaxed text-sm md:text-base italic">
                        "{testimonial.text}"
                      </p>
                    </CardContent>
                    <CardFooter className="flex flex-wrap gap-2 pt-0 mt-auto">
                      {testimonial.tags.map((tag, idx) => <span key={idx} data-index={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent text-primary text-xs font-medium font-default">
                          <CircleCheck className="size-3.5 text-primary" />
                          {tag}
                        </span>)}
                    </CardFooter>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:flex items-center justify-center gap-4 mt-10">
              <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 bg-card shadow-md hover:bg-primary hover:text-primary-foreground border-primary/20 transition-colors text-primary" />
              <CarouselNext className="static translate-y-0 translate-x-0 size-12 bg-card shadow-md hover:bg-primary hover:text-primary-foreground border-primary/20 transition-colors text-primary" />
            </div>
          </Carousel>
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <Button asChild size="lg" className="font-default rounded-md px-8 shadow-md text-base h-12 bg-primary text-primary-foreground hover:bg-secondary">
            <Link to="/ContactPage">
              <Star className="mr-2 size-5 fill-current" />
              Request Your Free Inspection
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}